<script setup lang="ts">
import { computed } from "vue";

import { ModalType } from "./interfaces";
import type { color } from "@/Modules/App/Models/Styles/Button";

import TcButton from "@/Components/UI/TcButton.vue";
import CrossIcon from "@/Components/Icons/CloseCircle.vue";

import { isValidString } from "@/Utils/String";

export interface TcModalProps {
  title?: string;
  textContent?: string;
  classContent?: string;
  class?: string;
  acceptButtonText?: string;
  rejectButtonText?: string;
  largeDescription?: boolean;
  acceptBtnStyle?: color;
  isLoading?: boolean;
  isAcceptButtonDisabled?: boolean;
  grow?: boolean;
  canClose?: boolean;
  type?: ModalType;
}

const props = withDefaults(defineProps<TcModalProps>(), {
  classContent: "",
  class: "",
  isAcceptButtonDisabled: false,
  canClose: false,
  type: ModalType.GLOBAL,
});

const emit = defineEmits<{
  (event: "acceptButtonClicked"): void;
  (event: "rejectButtonClicked"): void;
  (event: "clickOutside"): void;
}>();

const teleportTo = computed(() => {
  if (props.type === ModalType.GLOBAL) {
    return "#container";
  } else if (props.type === ModalType.MAIN) {
    return "#main";
  }

  return "#container";
});

const emitAcceptEvent = () => {
  emit("acceptButtonClicked");
};

const emitRejectEvent = () => {
  emit("rejectButtonClicked");
};

const emitClickOutsideEvent = () => {
  emit("clickOutside");
};
</script>

<template>
  <Teleport :to="teleportTo" defer>
    <div
      class="tc-modal"
      @click="emitClickOutsideEvent"
      :class="{
        [props.class]: isValidString(props.class),
        'tc-modal--main': props.type === ModalType.MAIN,
      }"
    >
      <div
        class="tc-modal-content"
        :class="{
          'only-cta': !rejectButtonText,
          'large-description': largeDescription,
          grow: props.grow,
        }"
        @click.stop
      >
        <CrossIcon v-if="canClose" class="tc-modal-content__close" @click="emitClickOutsideEvent" />

        <header class="title">
          <span>{{ title }}</span>
        </header>

        <main class="description" :class="classContent">
          <slot name="description" />

          <p v-if="isValidString(textContent)">{{ textContent }}</p>
        </main>

        <div class="actions" :class="{ 'actions--two': rejectButtonText && acceptButtonText }">
          <slot name="actions">
            <TcButton
              v-if="rejectButtonText"
              @click="emitRejectEvent"
              variant="outlined"
              class="reject-button"
              :isBlock="false"
              :isFitContent="true"
            >
              {{ rejectButtonText }}
            </TcButton>

            <TcButton
              v-if="acceptButtonText"
              :isLoading="isLoading"
              :disabled="isAcceptButtonDisabled"
              @click="emitAcceptEvent"
              class="accept-button"
              :color="acceptBtnStyle"
              :isBlock="false"
              :isFitContent="true"
            >
              {{ acceptButtonText }}
            </TcButton>
          </slot>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.tc-modal {
  display: grid;
  position: absolute;
  place-items: end;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  z-index: 100000;
  top: 0;
  right: 0;
  overflow-x: scroll;

  &--main {
    @include sp.padding(false, 4);

    width: 100% !important;
  }

  &-content {
    @include sp.padding(false, 6);
    @include colors.bg(background, base);

    position: relative;
    border-radius: $space-4 $space-4 0 0;
    display: grid;
    width: 100%;
    grid-template-columns: 1fr;
    grid-template-rows: repeat(3, auto);
    grid-template-areas:
      "title"
      "description"
      "actions";

    &__close {
      position: absolute;
      top: $space-2;
      right: $space-2;
      cursor: pointer;
    }
  }
}

.close-button {
  width: 20px;
  height: 20px;
}

@media (min-width: $breakpoint-m) {
  .tc-modal {
    place-items: center;
    overflow-x: hidden;

    &-content {
      border-radius: $space-4;
      margin: 0 auto;
      min-width: $size-modal-min-width-md;
      max-width: $size-modal-max-width-md;
      width: fit-content;

      grid-template-columns: 1fr;
      grid-template-rows: repeat(3, auto);
      grid-template-areas:
        "title"
        "description"
        "actions";

      &.only-cta {
        grid-template-areas:
          "title"
          "description"
          "actions";
      }

      &.large-description {
        @include sp.padding(false, 6);

        .description {
          @include sp.margin(y, 6);
        }
      }

      &.grow {
        max-width: fit-content;
      }

      .actions {
        flex-direction: row;
        gap: $space-4;
      }
    }
  }
}
</style>

<style lang="scss">
.v-dialog .v-overlay__content {
  @include sp.margin(false, 0);

  position: fixed;
  width: 100vw;
  left: 0;
  bottom: 0;
}

.tc-modal {
  .title {
    @include text.display(high, m);
    @include colors.color(content, high);

    grid-area: title;
  }

  .description {
    @include sp.margin(y, 4);
    @include text.general(low, l);
    @include colors.color(content, high);

    grid-area: description;
  }

  .actions {
    grid-area: actions;
    display: flex;
    justify-content: center;

    &--two {
      flex-direction: row;
      justify-content: space-around;
    }

    @media (max-width: $breakpoint-m) {
      flex-direction: column;
      gap: $space-4;

      button {
        width: 100%;
        display: flex;
        justify-content: center;
      }
    }
  }
}
</style>
